import React from 'react';
import clsx from 'clsx';
import { times } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { SelectPicker } from 'rsuite';

const numberPerPageArr = [
	{
		label: '5',
		value: '5'
	},
	{
		label: '10',
		value: '10'
	},
	{
		label: '15',
		value: '15'
	},
	{
		label: '20',
		value: '20'
	},
	{
		label: '25',
		value: '25'
	},
	{
		label: '30',
		value: '30'
	},
	{
		label: '50',
		value: '50'
	},
	{
		label: '100',
		value: '100'
	},
];

interface IPagination {
	pageCount: number;
	pageNumber: number;
	totalItemCount: number;
	hasNextPage: number | null;
	hasPreviousPage: number | null;
	pageSize: number;
	currentPage: number;
	setCurrentPage: any;
	pagesToShow?: number;
	rootPath?: string;
	numberPerPage?: string;
	setNumberPerPage?: (value: string) => void;
}

export const Pagination = (props: IPagination): JSX.Element => {
	const {
		pageNumber,
		totalItemCount,
		pageCount,
		hasNextPage,
		hasPreviousPage,
		pageSize,
		currentPage,
		setCurrentPage,
		pagesToShow,
		rootPath,
		numberPerPage,
		setNumberPerPage
	} = props;

	const navigate = useNavigate();

	const offset = pageSize;

	let lastValue = (pageNumber - 1) * offset + offset;
	if (lastValue > totalItemCount) {
		lastValue = totalItemCount;
	}

	const paginationText = `${(pageNumber - 1) * offset + 1} - ${lastValue} of ${totalItemCount}`;
	const pages = [];

	const changePage = (pageToNavigate: number): void => {
		if (pageCount >= pageToNavigate && pageToNavigate > 0) {
			setCurrentPage(pageToNavigate);
			navigate(`${rootPath}?page=${pageToNavigate}`);
		}
	};

	if (pageCount <= pagesToShow + 2) {
		times(pageCount - 2, i => {
			const pageNumber = i + 2;
			pages.push(
				<li
					className={clsx('page-item cursor-pointer', currentPage === pageNumber && 'active')}
			    onClick={(): void => changePage(pageNumber)}
			    key={pageNumber}
				>
          <span className="page-link">
            {pageNumber}
          </span>
				</li>
			);
		});
	} else {
		const firstPageNumber = (): number => {
			if (currentPage <= 2 ) {
				return 2;
			} else if (currentPage >= pageCount - pagesToShow) {
				return pageCount - pagesToShow;
			} else {
				return currentPage;
			}
		};
		const firstPage = firstPageNumber();
		const pagesDelta = pageCount - currentPage;
		const lastPage = firstPage + (pagesToShow - 1);
		if (firstPage > 2) {
			pages.push(
				<li className="page-item" key={1}>
          <span className="page-link">
            ...
          </span>
				</li>
			);
			// < 12345678 ... 11 >
			// < 1 ... 345678 ... 11 >
			// < 1 ... 45678910 11 >
		}
		for (let i = firstPage; i <= lastPage; i++) {
			pages.push(
				<li
					className={clsx('page-item cursor-pointer', currentPage === i && 'active')}
			    onClick={(): void => changePage(i)}
			    key={i}
				>
          <span className="page-link">
            {i}
          </span>
				</li>
			);
		}

		if (pagesDelta > pagesToShow) {
			pages.push(
				<li className="page-item" key={pageCount}>
          <span className="page-link">
            ...
          </span>
				</li>
			);
		}
	}

	const getPaginationText = ():JSX.Element => {
		if (numberPerPage) {
			return (
				<div className="d-flex align-items-center">
					<span className="fs-6 text-gray-700">Rows per page</span>
					<div className="mx-2">
						<SelectPicker
							className="p-0 select-picker"
							data={numberPerPageArr}
							value={numberPerPage}
							onChange={(selectorValue): void => setNumberPerPage(selectorValue)}
							searchable={false}
							cleanable={false}
						/>
					</div>
					<div className="fs-6 text-gray-700">
						{paginationText}
					</div>
				</div>
			);
		}
		return (
			<div className="fs-6 text-gray-700">
				{`Showing ${paginationText}`}
			</div>
		);
	};

	return (
		<div className="d-sm-flex flex-stack flex-wrap">
			{getPaginationText()}
			<ul className="pagination mt-5 mt-sm-0">
				{
					hasPreviousPage && (
						<li
							className="page-item previous cursor-pointer"
							onClick={(): void => changePage(pageNumber - 1)}
						>
              <span className="page-link">
                <i className="previous"/>
              </span>
						</li>
					)
				}
				<li className={clsx('page-item cursor-pointer', currentPage === 1 && 'active')}
				    onClick={(): void => changePage(1)}
				>
          <span className="page-link">
            1
          </span>
				</li>

				{pages}

				<li className={clsx('page-item cursor-pointer', currentPage === pageCount && 'active')}
				    onClick={(): void => changePage(pageCount)}
				>
          <span className="page-link">
            {pageCount}
          </span>
				</li>

				{
					hasNextPage && (
						<li
							className="page-item next cursor-pointer"
							onClick={(): void => changePage(pageNumber + 1)}
						>
              <span className="page-link">
                <i className="next"/>
              </span>
						</li>
					)
				}
			</ul>
		</div>
	);
};
